import { Direction } from './entity';
import type { QuestView, QuestRewards } from './quest';
import type { SoundBroadcastScope } from '../data/sounds';
import type {
  PartyView,
  PartyFinderEntry,
  PartyDistributionPolicy,
  PartyQuestPolicy,
  PartyVisibility,
} from '../data/party';
import type {
  ExchangeItemEntry,
  OrderBookLevel,
  MarketOrderView,
  MarketTradeView,
  MarketClaimView,
  MarketplaceFilters,
  MarketListingView,
  AppraisalResult,
  AffixOption,
  PriceCandle,
  SavedSearchView,
  MarketWatchView,
  MarketAlertView,
  TraderStats,
} from './market';

/**
 * Tipos de mensajes que el cliente puede enviar al servidor
 */
export enum ClientMessageType {
  MOVE = 'move',
  STOP_MOVE = 'stop_move',
  ATTACK = 'attack',
  USE_ITEM = 'use_item',
  EQUIP_ITEM = 'equip_item',
  UNEQUIP_ITEM = 'unequip_item',
  // Barco: embarcar/desembarcar (acción canalizada de 2s, tecla N). El server
  // decide embark vs disembark según el estado actual. Ver docs/WATER_AND_BOATS.md.
  TOGGLE_BOAT = 'toggle_boat',
  CHAT = 'chat',
  INVOKE_NPC = 'invoke_npc',
  REQUEST_NPC_TEMPLATES = 'request_npc_templates',
  INTERACT_NPC = 'interact_npc',
  INVENTORY_DROP = 'inventory_drop',
  INVENTORY_PICKUP = 'inventory_pickup',
  INVENTORY_MOVE_ITEM = 'inventory_move_item',
  REQUEST_ITEM_TEMPLATES = 'request_item_templates',
  SPAWN_ITEM_TEMPLATE = 'spawn_item_template',
  REQUEST_GROUND_ITEMS = 'request_ground_items',
  PLAY_ANIMATION = 'play_animation',
  REQUEST_CHARACTER_STATS = 'request_character_stats',
  REQUEST_TILE_INFO = 'request_tile_info',
  // Skill Tree (Espíritu)
  REQUEST_SKILL_TREE = 'request_skill_tree',
  ALLOCATE_SKILL_NODE = 'allocate_skill_node',
  RESET_SKILL_TREE = 'reset_skill_tree',
  // Skills de progresión (Minería/Herrería/…). Ver docs/SKILLS_SYSTEM.md.
  /** Pide el estado de todas las skills del personaje (XP + nivel). */
  REQUEST_SKILLS = 'request_skills',
  /** Extrae UNA vez de un yacimiento (compat). Server valida nivel/herramienta/adyacencia. */
  GATHER_RESOURCE = 'gather_resource',
  /** Inicia (o togglea) el MODO TRABAJO: auto-extracción de un nodo hasta agotarlo/moverse. */
  START_GATHER_WORK = 'start_gather_work',
  /** Detiene el modo trabajo activo. */
  STOP_GATHER_WORK = 'stop_gather_work',
  /** Craftea una receta en una estación (banco de carpintería, etc.). Server valida todo. */
  CRAFT_ITEM = 'craft_item',
  /** Repara todas las herramientas rotas ante el Maestro Herrero (cobra oro). */
  REPAIR_TOOLS = 'repair_tools',
  /** DEBUG (admin): otorga XP de una skill a un jugador. Ver docs/SKILLS_SYSTEM.md. */
  DEBUG_GRANT_SKILL_XP = 'debug_grant_skill_xp',
  // Spell System
  CAST_SPELL = 'cast_spell',
  TOGGLE_SPELL = 'toggle_spell',
  REQUEST_SPELL_BAR = 'request_spell_bar',
  SET_SPELL_BAR_SLOT = 'set_spell_bar_slot',
  REQUEST_LEARNED_SPELLS = 'request_learned_spells',
  TOGGLE_MEDITATE = 'toggle_meditate',
  // Invocaciones: cicla el modo de ataque de los summons (defensive→passive→
  // aggressive si está desbloqueado). Ver docs/SUMMONS.md.
  CYCLE_SUMMON_MODE = 'cycle_summon_mode',
  // Protección de aparición: el cliente avisa que un input de combate del local
  // (que no genera su propio mensaje, p.ej. hotkey de hechizo sin target) debe
  // romper la protección. Ver docs/SPAWN_PROTECTION.md.
  BREAK_SPAWN_PROTECTION = 'break_spawn_protection',
  // Target lock system (paradigma WoW, server-authoritative)
  SELECT_TARGET = 'select_target',
  // Admin/debug: override del bodyId visual del jugador (0 = restaurar)
  SET_BODY_OVERRIDE = 'set_body_override',
  // Admin/debug: ocultar/mostrar la head del jugador (para bodies de NPC sin head)
  SET_HEAD_HIDDEN = 'set_head_hidden',
  // Merchant (vendor de items)
  OPEN_MERCHANT = 'open_merchant',
  MERCHANT_BUY = 'merchant_buy',
  MERCHANT_SELL = 'merchant_sell',
  // Spell Vendor (vendor de hechizos)
  OPEN_SPELL_VENDOR = 'open_spell_vendor',
  BUY_SPELL = 'buy_spell',
  // Cierre genérico de cualquier vendor (item-merchant o spell-vendor).
  // Limpia el estado COMMERCING del NPC server-side.
  CLOSE_VENDOR = 'close_vendor',
  // VFX dev/test — toggle de aura sobre el local player (sin gameplay impact).
  // El server muta auraFxIds en PlayerState; todos los clientes ven el cambio.
  TOGGLE_AURA_FX = 'toggle_aura_fx',
  // Admin: teleport same-map al tile (x,y). El server valida player.isAdmin.
  ADMIN_TELEPORT = 'admin_teleport',
  // Facciones (ver docs/FACTIONS.md)
  /** El newbie acepta unirse a la facción ofrecida por el Reclutador. */
  JOIN_FACTION = 'join_faction',
  /** El player pide ascender de rango ante el Rey Faccionario. */
  ASCEND_RANK = 'ascend_rank',
  // Quests (ver docs/QUESTS.md)
  /** Pide el quest log completo (al entrar al mundo o al abrir el panel). */
  REQUEST_QUESTS = 'request_quests',
  /** Acepta una quest ofrecida por un NPC. */
  ACCEPT_QUEST = 'accept_quest',
  /** Entrega una quest ready en el NPC de turn-in. */
  TURN_IN_QUEST = 'turn_in_quest',
  /** Abandona una quest activa (la saca del log). */
  ABANDON_QUEST = 'abandon_quest',
  // Economía — Exchange (mercado de fungibles). Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
  /** Abre la Casa de Subastas (Exchange) ante el NPC Custodio. Server valida proximidad. */
  OPEN_EXCHANGE = 'open_exchange',
  /** Pide el libro + historial + órdenes propias de un itemId fungible. */
  EXCHANGE_VIEW_ITEM = 'exchange_view_item',
  /** Crea una orden de compra/venta (escrow de oro o ítems) y dispara el matching. */
  EXCHANGE_CREATE_ORDER = 'exchange_create_order',
  /** Cancela una orden propia; lo no ejecutado vuelve al Cajón de Retiro. */
  EXCHANGE_CANCEL_ORDER = 'exchange_cancel_order',
  /** Reclama todo el Cajón de Retiro (oro al balance, ítems al inventario). */
  EXCHANGE_CLAIM_ALL = 'exchange_claim_all',
  // Marketplace (ítems rolled, modelo PoE Trade).
  /** Abre el Bazar (Marketplace) ante el NPC Custodio. */
  OPEN_MARKETPLACE = 'open_marketplace',
  /** Busca listados con filtros avanzados (afijos/rareza/slot/precio). */
  MARKETPLACE_SEARCH = 'marketplace_search',
  /** Publica una instancia del inventario (escrow) a un precio fijo. */
  MARKETPLACE_PUBLISH = 'marketplace_publish',
  /** Compra un listado por su id (escrow de oro → entrega física). */
  MARKETPLACE_BUY = 'marketplace_buy',
  /** Cancela un listado propio (la instancia vuelve al Cajón). */
  MARKETPLACE_CANCEL = 'marketplace_cancel',
  /** Pide la tasación automática de una instancia del inventario. */
  MARKETPLACE_APPRAISE = 'marketplace_appraise',
  // Perfil de comerciante (búsquedas guardadas, watchlist, alertas, Deal Finder).
  /** Guarda una búsqueda (opcionalmente como alerta). */
  MARKETPLACE_SAVE_SEARCH = 'marketplace_save_search',
  /** Borra una búsqueda guardada. */
  MARKETPLACE_DELETE_SEARCH = 'marketplace_delete_search',
  /** Pide ofertas (Deal Finder): listados por debajo de su valor de mercado. */
  MARKETPLACE_DEALS = 'marketplace_deals',
  /** Agrega una vigilancia de precio del Exchange. */
  EXCHANGE_ADD_WATCH = 'exchange_add_watch',
  /** Quita una vigilancia de precio. */
  EXCHANGE_REMOVE_WATCH = 'exchange_remove_watch',
  /** Marca todas las alertas como vistas. */
  MARKET_ALERTS_SEEN = 'market_alerts_seen',
  /** Pide la cartera del comerciante (historial + P&L + stats). */
  REQUEST_TRADER_STATS = 'request_trader_stats',
  // Audio (visor de sonidos admin). Ver docs/SOUND_AND_MUSIC.md.
  /**
   * Admin: reproducir un sonido a otros jugadores desde el visor de sonidos. El server valida
   * `player.isAdmin` y resuelve destinatarios según `scope` (screen/zone/map/all).
   */
  ADMIN_PLAY_SOUND = 'admin_play_sound',
  // Party (grupos entre jugadores). Ver docs/PARTY_SYSTEM.md.
  /** Pide el estado del propio party (al entrar o abrir el panel). */
  REQUEST_PARTY = 'request_party',
  /** Invita a otro jugador (por sessionId) a tu party (crea uno si no tenés). */
  PARTY_INVITE = 'party_invite',
  /** Responde una invitación pendiente (aceptar/rechazar). */
  PARTY_INVITE_RESPONSE = 'party_invite_response',
  /** Salir del party propio. */
  PARTY_LEAVE = 'party_leave',
  /** Líder: expulsa a un miembro (por sessionId). */
  PARTY_KICK = 'party_kick',
  /** Líder: transfiere el liderazgo a otro miembro. */
  PARTY_TRANSFER_LEADER = 'party_transfer_leader',
  /** Líder: reordena el party (define prioridad de crédito de quest). */
  PARTY_REORDER = 'party_reorder',
  /** Líder: cambia una o más políticas de reparto (oro/loot/quest). */
  PARTY_SET_POLICY = 'party_set_policy',
  /** Portador de `partyXpControl`: fija el reparto custom de XP (o lo resetea). */
  PARTY_SET_XP_SHARES = 'party_set_xp_shares',
  /** Líder: disuelve el party entero. */
  PARTY_DISBAND = 'party_disband',
  /** Party Finder: pide la lista de parties públicas. */
  PARTY_FINDER_LIST = 'party_finder_list',
  /** Party Finder: crea un party propio (arranca solo). */
  PARTY_FINDER_CREATE = 'party_finder_create',
  /** Party Finder: cambia visibilidad (public/closed) y tag de nivel. */
  PARTY_FINDER_SET_VISIBILITY = 'party_finder_set_visibility',
  /** Party Finder: se une a un party público por id. */
  PARTY_FINDER_JOIN = 'party_finder_join',
}

/**
 * Mensaje de movimiento del cliente
 */
export interface MoveMessage {
  type: ClientMessageType.MOVE;
  direction: Direction;
}

export interface StopMoveMessage {
  type: ClientMessageType.STOP_MOVE;
}

/**
 * Mensaje de ataque del cliente (golpe melee direccional).
 *
 * El melee es por heading, NO por target lock: el cliente manda la dirección que
 * mira el personaje y el server golpea las tiles del Radio de ataque en ese
 * sentido (cleave). Ver docs/COMBAT_SYSTEMS.md.
 */
export interface AttackMessage {
  type: ClientMessageType.ATTACK;
  direction: Direction;
}

/**
 * Mensaje de uso de item
 */
export interface UseItemMessage {
  type: ClientMessageType.USE_ITEM;
  itemId: string;
}

/**
 * Mensaje de equipar item
 */
export interface EquipItemMessage {
  type: ClientMessageType.EQUIP_ITEM;
  itemId: string;
  slot: string;
}

/**
 * Mensaje de desequipar item
 */
export interface UnequipItemMessage {
  type: ClientMessageType.UNEQUIP_ITEM;
  slot: string;
}

/**
 * Barco: embarcar/desembarcar. Sin payload — el server decide la dirección
 * según si el player está `embarked`. Ver docs/WATER_AND_BOATS.md.
 */
export interface ToggleBoatMessage {
  type: ClientMessageType.TOGGLE_BOAT;
}

/**
 * Mensaje de chat
 */
export interface ChatMessage {
  type: ClientMessageType.CHAT;
  message: string;
}

export interface InvokeNpcMessage {
  type: ClientMessageType.INVOKE_NPC;
  /**
   * ID lógico del NPC/template a invocar (por ejemplo "npc_001").
   */
  npcTemplateId: string;
}

export interface RequestNpcTemplatesMessage {
  type: ClientMessageType.REQUEST_NPC_TEMPLATES;
}

export interface InteractNpcMessage {
  type: ClientMessageType.INTERACT_NPC;
  npcId: string;
}

// =========================================================================
// SKILLS de progresión (Minería/Herrería/…) — ver docs/SKILLS_SYSTEM.md
// =========================================================================

/** Pide el estado completo de skills del personaje. */
export interface RequestSkillsMessage {
  type: ClientMessageType.REQUEST_SKILLS;
}

/** Extrae de un yacimiento (minar). Server valida nivel/pico/adyacencia. */
export interface GatherResourceMessage {
  type: ClientMessageType.GATHER_RESOURCE;
  nodeId: string;
}

/** Inicia/togglea el modo trabajo (auto-extracción) sobre un nodo. */
export interface StartGatherWorkMessage {
  type: ClientMessageType.START_GATHER_WORK;
  nodeId: string;
}

/** Detiene el modo trabajo activo. */
export interface StopGatherWorkMessage {
  type: ClientMessageType.STOP_GATHER_WORK;
}

/** Craftea una receta en una estación. Server valida proximidad/skill/materiales. */
export interface CraftItemMessage {
  type: ClientMessageType.CRAFT_ITEM;
  stationId: string;
  recipeId: string;
}

/** Repara todas las herramientas rotas ante el Maestro Herrero (cobra oro). */
export interface RepairToolsMessage {
  type: ClientMessageType.REPAIR_TOOLS;
  npcId: string;
}

/** DEBUG (admin): otorga XP de una skill a un jugador (default: uno mismo). */
export interface DebugGrantSkillXpMessage {
  type: ClientMessageType.DEBUG_GRANT_SKILL_XP;
  /** sessionId del jugador objetivo. '' o ausente = uno mismo. */
  targetSessionId?: string;
  skillId: string;
  amount: number;
}

/** Una entrada de skill (XP + nivel derivado). */
export interface SkillSyncEntryMsg {
  skillId: string;
  experience: number;
  level: number;
}

/** Estado completo de skills (Server → Cliente). */
export interface SkillsSyncMessage {
  type: ServerMessageType.SKILLS_SYNC;
  skills: SkillSyncEntryMsg[];
}

/** XP ganada en una skill (privado al player). */
export interface SkillXpGainedMessage {
  type: ServerMessageType.SKILL_XP_GAINED;
  skillId: string;
  amount: number;
  experience: number;
  level: number;
}

/** Una skill subió de nivel. */
export interface SkillLevelUpMessage {
  type: ServerMessageType.SKILL_LEVEL_UP;
  skillId: string;
  newLevel: number;
}

/** Aviso overhead coloreado sobre el propio personaje. `color` es un hex CSS. */
export interface SkillNoticeMessage {
  type: ServerMessageType.SKILL_NOTICE;
  text: string;
  color: string;
}

/** Empezó el modo trabajo sobre un nodo: el cliente abre la ventanita de progreso. */
export interface WorkStartedMessage {
  type: ServerMessageType.WORK_STARTED;
  nodeId: string;
}

/** Terminó el modo trabajo: el cliente cierra la ventanita. `reason` para debug/feedback. */
export interface WorkStoppedMessage {
  type: ServerMessageType.WORK_STOPPED;
  reason: 'depleted' | 'moved' | 'tool' | 'level' | 'adjacency' | 'inventory' | 'manual' | 'error';
}

// =========================================================================
// MERCHANT (vendor) — Cliente → Server
// =========================================================================

/** Pide abrir el MerchantPanel de un NPC. Server valida proximidad. */
export interface OpenMerchantMessage {
  type: ClientMessageType.OPEN_MERCHANT;
  npcId: string;
}

/** Player compra `quantity` unidades de `itemId` al vendor del NPC. */
export interface MerchantBuyMessage {
  type: ClientMessageType.MERCHANT_BUY;
  npcId: string;
  itemId: string;
  quantity: number;
}

/** Player vende del slot `slotIndex` `quantity` unidades al vendor del NPC. */
export interface MerchantSellMessage {
  type: ClientMessageType.MERCHANT_SELL;
  slotIndex: number;
  npcId: string;
  quantity: number;
}

// =========================================================================
// SPELL VENDOR — Cliente → Server
// =========================================================================

/** Pide abrir el SpellVendorPanel de un NPC. Server valida proximidad. */
export interface OpenSpellVendorMessage {
  type: ClientMessageType.OPEN_SPELL_VENDOR;
  npcId: string;
}

/** Player compra (aprende) un spell del vendor del NPC. Consume oro + itemCosts. */
export interface BuySpellMessage {
  type: ClientMessageType.BUY_SPELL;
  npcId: string;
  spellId: string;
}

/**
 * Cierre genérico de un vendor abierto (item-merchant o spell-vendor).
 * Server limpia el estado COMMERCING del NPC si no hay más players con el panel abierto.
 */
export interface CloseVendorMessage {
  type: ClientMessageType.CLOSE_VENDOR;
  npcId: string;
}


// =========================================================================
// EXCHANGE (mercado de fungibles) — Cliente → Server
// =========================================================================

/** Pide abrir la Casa de Subastas (Exchange) ante el NPC Custodio. */
export interface OpenExchangeMessage {
  type: ClientMessageType.OPEN_EXCHANGE;
  npcId: string;
}

/** Pide el libro de órdenes + historial + órdenes propias de un itemId fungible. */
export interface ExchangeViewItemMessage {
  type: ClientMessageType.EXCHANGE_VIEW_ITEM;
  npcId: string;
  itemId: string;
}

/** Crea una orden de compra o venta en el libro de un itemId fungible. */
export interface ExchangeCreateOrderMessage {
  type: ClientMessageType.EXCHANGE_CREATE_ORDER;
  npcId: string;
  itemId: string;
  side: 'buy' | 'sell';
  /** Oro por unidad (límite). */
  price: number;
  quantity: number;
}

/** Cancela una orden propia abierta. Lo no ejecutado va al Cajón de Retiro. */
export interface ExchangeCancelOrderMessage {
  type: ClientMessageType.EXCHANGE_CANCEL_ORDER;
  npcId: string;
  orderId: string;
}

/** Reclama todo el Cajón de Retiro (oro al balance, ítems al inventario). */
export interface ExchangeClaimAllMessage {
  type: ClientMessageType.EXCHANGE_CLAIM_ALL;
  npcId: string;
}

// =========================================================================
// MARKETPLACE (ítems rolled) — Cliente → Server
// =========================================================================

export interface OpenMarketplaceMessage {
  type: ClientMessageType.OPEN_MARKETPLACE;
  npcId: string;
}

export interface MarketplaceSearchMessage {
  type: ClientMessageType.MARKETPLACE_SEARCH;
  npcId: string;
  filters: MarketplaceFilters;
}

export interface MarketplacePublishMessage {
  type: ClientMessageType.MARKETPLACE_PUBLISH;
  npcId: string;
  slotIndex: number;
  price: number;
}

export interface MarketplaceBuyMessage {
  type: ClientMessageType.MARKETPLACE_BUY;
  npcId: string;
  listingId: string;
}

export interface MarketplaceCancelMessage {
  type: ClientMessageType.MARKETPLACE_CANCEL;
  npcId: string;
  listingId: string;
}

export interface MarketplaceAppraiseMessage {
  type: ClientMessageType.MARKETPLACE_APPRAISE;
  npcId: string;
  slotIndex: number;
}

// Perfil de comerciante — Cliente → Server
export interface MarketplaceSaveSearchMessage {
  type: ClientMessageType.MARKETPLACE_SAVE_SEARCH;
  npcId: string;
  name: string;
  filters: MarketplaceFilters;
  alert: boolean;
}

export interface MarketplaceDeleteSearchMessage {
  type: ClientMessageType.MARKETPLACE_DELETE_SEARCH;
  npcId: string;
  searchId: string;
}

export interface MarketplaceDealsMessage {
  type: ClientMessageType.MARKETPLACE_DEALS;
  npcId: string;
  filters: MarketplaceFilters;
}

export interface ExchangeAddWatchMessage {
  type: ClientMessageType.EXCHANGE_ADD_WATCH;
  npcId: string;
  itemId: string;
  direction: 'below' | 'above';
  targetPrice: number;
}

export interface ExchangeRemoveWatchMessage {
  type: ClientMessageType.EXCHANGE_REMOVE_WATCH;
  npcId: string;
  watchId: string;
}

export interface MarketAlertsSeenMessage {
  type: ClientMessageType.MARKET_ALERTS_SEEN;
  npcId: string;
}

export interface RequestTraderStatsMessage {
  type: ClientMessageType.REQUEST_TRADER_STATS;
  npcId: string;
}

export interface InventoryDropMessage {
  type: ClientMessageType.INVENTORY_DROP;
  /**
   * Índice de slot en el inventario del que se va a tirar (drop) el item.
   */
  slotIndex: number;
  /**
   * Cantidad a tirar. Opcional para compatibilidad; default = 1 si no se envía.
   */
  quantity?: number;
}

export interface InventoryPickupMessage {
  type: ClientMessageType.INVENTORY_PICKUP;
  /**
   * ID del item en el piso que se quiere levantar.
   */
  groundItemId: string;
}

export interface InventoryMoveItemMessage {
  type: ClientMessageType.INVENTORY_MOVE_ITEM;
  /**
   * Slot de origen (índice del inventario)
   */
  fromSlot: number;
  /**
   * Slot de destino (índice del inventario)
   */
  toSlot: number;
}

export interface RequestItemTemplatesMessage {
  type: ClientMessageType.REQUEST_ITEM_TEMPLATES;
}

export interface SpawnItemTemplateMessage {
  type: ClientMessageType.SPAWN_ITEM_TEMPLATE;
  /**
   * ID lógico del item template (coincide con items.id en DB).
   */
  itemId: string;
}

export interface RequestGroundItemsMessage {
  type: ClientMessageType.REQUEST_GROUND_ITEMS;
}

/**
 * Admin: reproducir un sonido a otros jugadores desde el visor de sonidos in-game. El server valida
 * `player.isAdmin` y usa la posición/área del propio admin para resolver `screen`/`zone`. Ver
 * docs/SOUND_AND_MUSIC.md.
 */
export interface AdminPlaySoundMessage {
  type: ClientMessageType.ADMIN_PLAY_SOUND;
  soundId: string;
  scope: SoundBroadcastScope;
}

export interface ClientPlayAnimationMessage {
  type: ClientMessageType.PLAY_ANIMATION;
  animationId: number;
}

/**
 * Toggle aura VFX sobre el local player. Sin gameplay impact — solo muta
 * `auraFxIds` en PlayerState; los clientes ven el cambio sincronizado.
 */
export interface ClientToggleAuraFxMessage {
  type: ClientMessageType.TOGGLE_AURA_FX;
  fxId: string;
}

export interface RequestCharacterStatsMessage {
  type: ClientMessageType.REQUEST_CHARACTER_STATS;
}

export interface RequestTileInfoMessage {
  type: ClientMessageType.REQUEST_TILE_INFO;
  x: number;
  y: number;
}

// ============================================================================
// Skill Tree (Espíritu) Messages
// ============================================================================

/**
 * Solicitar el estado actual del skill tree del personaje
 */
export interface RequestSkillTreeMessage {
  type: ClientMessageType.REQUEST_SKILL_TREE;
}

/**
 * Asignar un nodo del skill tree
 */
export interface AllocateSkillNodeMessage {
  type: ClientMessageType.ALLOCATE_SKILL_NODE;
  nodeId: string;
}

/**
 * Resetear todos los nodos del skill tree
 */
export interface ResetSkillTreeMessage {
  type: ClientMessageType.RESET_SKILL_TREE;
}

// ============================================================================
// Spell System Messages (Client → Server)
// ============================================================================

export interface CastSpellMessage {
  type: ClientMessageType.CAST_SPELL;
  spellId: string;
  targetId?: string;      // for target spells (entity sessionId or npcId)
  targetX?: number;       // for area/ground spells
  targetY?: number;
}

export interface ToggleSpellMessage {
  type: ClientMessageType.TOGGLE_SPELL;
  spellId: string;
}

/** Cicla el modo de ataque de las invocaciones del jugador. Ver docs/SUMMONS.md. */
export interface CycleSummonModeMessage {
  type: ClientMessageType.CYCLE_SUMMON_MODE;
}

/** Respuesta del server con el modo de ataque de summons actual y si el agresivo está desbloqueado. */
export interface SummonModeChangedMessage {
  type: ServerMessageType.SUMMON_MODE_CHANGED;
  /** 'defensive' | 'passive' | 'aggressive' (ver SummonMode en shared/types/summon.ts). */
  mode: string;
  /** True si el jugador tiene desbloqueado el modo agresivo (amuleto o keystone). */
  aggressiveUnlocked: boolean;
}

export interface RequestSpellBarMessage {
  type: ClientMessageType.REQUEST_SPELL_BAR;
}

export interface SetSpellBarSlotMessage {
  type: ClientMessageType.SET_SPELL_BAR_SLOT;
  slotIndex: number;
  spellId: string;
  /**
   * true = movimiento/intercambio interno de la barra (el hechizo ya estaba en la
   * hotbar): NO dispara el cooldown de asignación. Ausente/false = asignación nueva.
   */
  isSwap?: boolean;
}

export interface RequestLearnedSpellsMessage {
  type: ClientMessageType.REQUEST_LEARNED_SPELLS;
}

/**
 * Target lock: el cliente pide al server lockear un target.
 * Server valida que la entity exista y esté dentro del rango de visibilidad
 * antes de actualizar PlayerState.targetId. Pasar targetId vacío o null = clear.
 */
export interface SelectTargetMessage {
  type: ClientMessageType.SELECT_TARGET;
  targetId: string | null;
}

/**
 * Admin/debug: setea un override del bodyId del jugador.
 * bodyId = 0 limpia el override y restaura el body según equipamiento.
 */
export interface SetBodyOverrideMessage {
  type: ClientMessageType.SET_BODY_OVERRIDE;
  bodyId: number;
}

/**
 * Admin/debug: oculta o muestra la head del jugador. Útil para vestir bodies de
 * NPC (criaturas) que ya incluyen la cabeza en el sheet. Runtime-only (no persiste).
 */
export interface SetHeadHiddenMessage {
  type: ClientMessageType.SET_HEAD_HIDDEN;
  hidden: boolean;
}

/**
 * Admin: teleport same-map al tile (x,y). El server valida player.isAdmin antes
 * de aplicar el snap; si no es admin responde ERROR con code NOT_ADMIN.
 */
export interface AdminTeleportMessage {
  type: ClientMessageType.ADMIN_TELEPORT;
  x: number;
  y: number;
}

/**
 * Snapshot simple de un slot de inventario para sincronizar al cliente.
 */
export interface InventorySlotData {
  slotIndex: number;
  itemId: string;
  enchantId: string | null;
  quantity: number;
  /** Rareza rolled de la instancia (ItemRarity como number). */
  rolledRarity: number;
  /**
   * Afijos rolled de la instancia. Mapa de modifier key → componentes (cada
   * componente tiene `v` (valor) y `t` (tier 1-3)). Vacío `{}` si no aplica.
   * Ver `RolledAffixes` en `shared/types/item.ts`.
   */
  rolledAffixes: Record<string, Array<{ v: number; t: number }>>;
  /** Si el item está equipado, indica en qué slot de equipamiento (ej: 'body', 'weapon') */
  equippedIn?: string;
}

// ============================================================================
// Facciones (ver docs/FACTIONS.md)
// ============================================================================

/** El newbie confirma unirse a la facción ofrecida por el Reclutador. */
export interface JoinFactionMessage {
  type: ClientMessageType.JOIN_FACTION;
  /** Facción a la que se une ('aresgard' | 'elvaria'). */
  factionId: string;
}

/** El player pide ascender de rango ante el Rey Faccionario. */
export interface AscendRankMessage {
  type: ClientMessageType.ASCEND_RANK;
  /** ID del NPC rey con el que está interactuando (validación de cercanía). */
  npcId: string;
}

// ===========================================================================
// QUESTS (cliente → servidor). Ver docs/QUESTS.md.
// ===========================================================================

export interface RequestQuestsMessage {
  type: ClientMessageType.REQUEST_QUESTS;
}

export interface AcceptQuestMessage {
  type: ClientMessageType.ACCEPT_QUEST;
  questId: string;
  /** NPC desde el que se acepta (validación de cercanía/oferta). */
  npcId: string;
}

export interface TurnInQuestMessage {
  type: ClientMessageType.TURN_IN_QUEST;
  questId: string;
  /** NPC de turn-in (validación de cercanía). */
  npcId: string;
}

export interface AbandonQuestMessage {
  type: ClientMessageType.ABANDON_QUEST;
  questId: string;
}

/**
 * Unión de todos los tipos de mensajes del cliente
 */
export type ClientMessage =
  | MoveMessage
  | StopMoveMessage
  | AttackMessage
  | UseItemMessage
  | EquipItemMessage
  | ToggleBoatMessage
  | ChatMessage
  | InvokeNpcMessage
  | RequestNpcTemplatesMessage
  | InteractNpcMessage
  | OpenMerchantMessage
  | MerchantBuyMessage
  | MerchantSellMessage
  | OpenSpellVendorMessage
  | BuySpellMessage
  | CloseVendorMessage
  | InventoryDropMessage
  | InventoryPickupMessage
  | RequestItemTemplatesMessage
  | SpawnItemTemplateMessage
  | RequestGroundItemsMessage
  | AdminPlaySoundMessage
  | ClientPlayAnimationMessage
  | ClientToggleAuraFxMessage
  | RequestCharacterStatsMessage
  | RequestTileInfoMessage
  | RequestSkillTreeMessage
  | AllocateSkillNodeMessage
  | ResetSkillTreeMessage
  | CastSpellMessage
  | ToggleSpellMessage
  | RequestSpellBarMessage
  | SetSpellBarSlotMessage
  | RequestLearnedSpellsMessage
  | ToggleMeditateMessage
  | BreakSpawnProtectionMessage
  | SelectTargetMessage
  | SetBodyOverrideMessage
  | SetHeadHiddenMessage
  | AdminTeleportMessage
  | JoinFactionMessage
  | AscendRankMessage
  | RequestQuestsMessage
  | AcceptQuestMessage
  | TurnInQuestMessage
  | AbandonQuestMessage
  | OpenExchangeMessage
  | ExchangeViewItemMessage
  | ExchangeCreateOrderMessage
  | ExchangeCancelOrderMessage
  | ExchangeClaimAllMessage
  | OpenMarketplaceMessage
  | MarketplaceSearchMessage
  | MarketplacePublishMessage
  | MarketplaceBuyMessage
  | MarketplaceCancelMessage
  | MarketplaceAppraiseMessage
  | MarketplaceSaveSearchMessage
  | MarketplaceDeleteSearchMessage
  | MarketplaceDealsMessage
  | ExchangeAddWatchMessage
  | ExchangeRemoveWatchMessage
  | MarketAlertsSeenMessage
  | RequestTraderStatsMessage
  | RequestPartyMessage
  | PartyInviteMessage
  | PartyInviteResponseMessage
  | PartyLeaveMessage
  | PartyKickMessage
  | PartyTransferLeaderMessage
  | PartyReorderMessage
  | PartySetPolicyMessage
  | PartySetXpSharesMessage
  | PartyDisbandMessage
  | PartyFinderListMessage
  | PartyFinderCreateMessage
  | PartyFinderSetVisibilityMessage
  | PartyFinderJoinMessage;

/**
 * Tipos de mensajes que el servidor puede enviar al cliente
 */
export enum ServerMessageType {
  MOVEMENT_CONFIRMED = 'movement_confirmed',
  ATTACK_RESULT = 'attack_result',
  /** Confirma al cliente el modo de ataque actual de sus invocaciones. Ver docs/SUMMONS.md. */
  SUMMON_MODE_CHANGED = 'summon_mode_changed',
  /** Broadcast de swing melee (telegraph): una entidad golpeó en una dirección. */
  MELEE_SWING = 'melee_swing',
  /** Telegrafía de AoE: zona (centro + tamaño) donde va a caer un hechizo de área. */
  AOE_TELEGRAPH = 'aoe_telegraph',
  ENTITY_UPDATE = 'entity_update',
  CHAT_MESSAGE = 'chat_message',
  ERROR = 'error',
  /**
   * Actualización de estado de conexión de personajes (login screen)
   */
  CHARACTER_STATUS_UPDATE = 'character_status_update',
  NPC_TEMPLATE_LIST = 'npc_template_list',
  NPC_DIALOGUE = 'npc_dialogue',
  ITEM_TEMPLATE_LIST = 'item_template_list',
  GROUND_ITEMS_SYNC = 'ground_items_sync',
  INVENTORY_SYNC = 'inventory_sync',
  /**
   * Reproducir animación sobre una entidad
   */
  PLAY_ANIMATION = 'play_animation',
  CHARACTER_STATS = 'character_stats',
  TILE_INFO = 'tile_info',
  // Skill Tree (Espíritu)
  SKILL_TREE_SYNC = 'skill_tree_sync',
  SKILL_NODE_ALLOCATED = 'skill_node_allocated',
  SKILL_TREE_RESET = 'skill_tree_reset',
  // Skills de progresión (Minería/Herrería/…). Ver docs/SKILLS_SYSTEM.md.
  /** Estado completo de todas las skills del personaje (XP + nivel). */
  SKILLS_SYNC = 'skills_sync',
  /** XP ganada en una skill (bubble "+N Minería" + refresco del panel). */
  SKILL_XP_GAINED = 'skill_xp_gained',
  /** Una skill subió de nivel (efecto + bubble). */
  SKILL_LEVEL_UP = 'skill_level_up',
  /** Aviso overhead coloreado sobre el propio personaje (gates de minería, rotura de pico). */
  SKILL_NOTICE = 'skill_notice',
  /** Empezó el modo trabajo sobre un nodo (abre la ventanita de progreso). */
  WORK_STARTED = 'work_started',
  /** Terminó el modo trabajo (cierra la ventanita). */
  WORK_STOPPED = 'work_stopped',
  // Spell System
  SPELL_CAST_RESULT = 'spell_cast_result',
  SPELL_LEARNED = 'spell_learned',
  SPELL_BAR_SYNC = 'spell_bar_sync',
  LEARNED_SPELLS_SYNC = 'learned_spells_sync',
  AURA_TOGGLED = 'aura_toggled',
  CAST_STARTED = 'cast_started',
  CAST_INTERRUPTED = 'cast_interrupted',
  /** Barco: arranca el canal de embarque/desembarque (barra 2s sobre el char). */
  BOAT_CHANNEL_STARTED = 'boat_channel_started',
  /** Barco: el canal se interrumpió (p.ej. por movimiento) antes de completarse. */
  BOAT_CHANNEL_INTERRUPTED = 'boat_channel_interrupted',
  /** Spell words shown above caster (dark cyan, separate from chat) */
  SPELL_WORDS = 'spell_words',
  /** Active buffs/debuffs sync for a player (sent periodically + on change) */
  ACTIVE_BUFFS_SYNC = 'active_buffs_sync',
  MEDITATE_STATE = 'meditate_state',
  /** Protección de aparición activa/inactiva para una entidad (aura + viñeta + FX). */
  SPAWN_PROTECTION_STATE = 'spawn_protection_state',
  LEVEL_UP = 'level_up',
  /** Ganancia de experiencia, enviado SOLO al player que la cobró (bubble overhead "+N EXP"). */
  EXP_GAINED = 'exp_gained',
  /** Merchant (vendor) abierto: server confirma + envía lista de items con precios. */
  MERCHANT_OPENED = 'merchant_opened',
  /** Spell vendor abierto: server confirma + envía lista de spells con precios e items requeridos. */
  SPELL_VENDOR_OPENED = 'spell_vendor_opened',
  /** Update de oro del player (privado): tras buy/sell/loot/gold reward. */
  GOLD_UPDATED = 'gold_updated',
  /** Un NPC con notifyOnRespawn=true respawneó. Broadcast a toda la sala. */
  NPC_RESPAWNED = 'npc_respawned',
  /** Translado same-map: snap atómico del player + animación de llegada */
  PLAYER_TELEPORTED = 'player_teleported',
  /** Translado cross-map: el cliente debe leave + rejoin a la room del nuevo mapa */
  PLAYER_MAP_CHANGE = 'player_map_change',
  /** One-shot FX disparado por el server sobre una entidad (player o NPC) — broadcast AOI. */
  PLAY_ENTITY_FX = 'play_entity_fx',
  /**
   * One-shot de sonido disparado por el server (hoy: visor de sonidos admin). Si trae `x`/`y`,
   * el cliente lo reproduce posicional; si no, 2D. Ver docs/SOUND_AND_MUSIC.md.
   */
  PLAY_SOUND = 'play_sound',
  /** El player entró/salió del "área-banner". Privado al player. Banner + música. */
  AREA_ENTERED = 'area_entered',
  /** Entrada a un área denegada por requisitos (item/clase/raza/horario). Privado. */
  AREA_ENTRY_DENIED = 'area_entry_denied',
  // Facciones (ver docs/FACTIONS.md)
  /** Stats de facción privados del dueño (honor, enemigos eliminados, rango). */
  FACTION_STATS = 'faction_stats',
  /** El Reclutador ofrece unirse a una facción. El cliente muestra confirmación. */
  FACTION_JOIN_PROMPT = 'faction_join_prompt',
  /** Info de rango para el panel del Rey Faccionario (rango, kills, próximo umbral). */
  FACTION_RANK_INFO = 'faction_rank_info',
  /** Notificación broadcast solo a los players de una facción (ascensos, etc.). */
  FACTION_BROADCAST = 'faction_broadcast',
  // Quests (ver docs/QUESTS.md)
  /** Quest log completo del player (activas + ready). El cliente lo rendea en log/tracker. */
  QUEST_LOG_SYNC = 'quest_log_sync',
  /** Un NPC ofrece una quest. El cliente abre el QuestDialogPanel en modo oferta. */
  QUEST_OFFER = 'quest_offer',
  /** Avance de una quest (un objetivo subió o la quest pasó a ready). Actualiza tracker/log. */
  QUEST_PROGRESS = 'quest_progress',
  /** Quest entregada: trae las recompensas otorgadas para feedback. */
  QUEST_COMPLETED = 'quest_completed',
  // Economía — Exchange (mercado de fungibles). Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
  /** Casa de Subastas abierta: catálogo de fungibles + oro + cajón de retiro. */
  EXCHANGE_OPENED = 'exchange_opened',
  /** Libro de órdenes + historial + órdenes propias de un itemId. */
  EXCHANGE_BOOK = 'exchange_book',
  /** Cajón de Retiro actualizado (tras crear/cancelar/reclamar). */
  EXCHANGE_CLAIMS = 'exchange_claims',
  // Marketplace (ítems rolled).
  /** Bazar abierto: vocabulario de filtros (afijos/subtipos/slots) + oro + cajón. */
  MARKETPLACE_OPENED = 'marketplace_opened',
  /** Resultados de búsqueda de listados. */
  MARKETPLACE_RESULTS = 'marketplace_results',
  /** Tasación automática de una instancia (rango sugerido + comparables). */
  MARKETPLACE_APPRAISAL = 'marketplace_appraisal',
  // Perfil de comerciante.
  /** Lista de búsquedas guardadas del player. */
  MARKETPLACE_SAVED_SEARCHES = 'marketplace_saved_searches',
  /** Lista de vigilancias de precio del player. */
  EXCHANGE_WATCHES = 'exchange_watches',
  /** Alertas del player (saved-search match o price watch). */
  MARKET_ALERTS = 'market_alerts',
  /** Cartera del comerciante (historial + P&L + stats). */
  TRADER_STATS = 'trader_stats',
  // Party (grupos entre jugadores). Ver docs/PARTY_SYSTEM.md.
  /** Estado completo del party del jugador (o vacío = sin party). Rendea roster/ventana. */
  PARTY_SYNC = 'party_sync',
  /** Llega una invitación de party (para el toast Aceptar/Rechazar). */
  PARTY_INVITE_RECEIVED = 'party_invite_received',
  /** Lista de parties públicas para el Party Finder. */
  PARTY_FINDER_RESULTS = 'party_finder_results',
  /** Feedback/errores de party en texto (privado al player). */
  PARTY_NOTIFY = 'party_notify',
}

/**
 * Mensaje de confirmación de movimiento
 */
export interface MovementConfirmedMessage {
  type: ServerMessageType.MOVEMENT_CONFIRMED;
  x: number;
  y: number;
  direction: Direction;
}

/**
 * Mensaje de resultado de ataque
 */
export interface AttackResultMessage {
  type: ServerMessageType.ATTACK_RESULT;
  targetId: string;
  damage: number;
  isCrit: boolean;
  targetHp: number;
  /** El golpe fue esquivado (fallo). El cliente reproduce el "whoosh". */
  evaded?: boolean;
  /** El golpe fue bloqueado con escudo. El cliente reproduce el sonido de bloqueo. */
  blocked?: boolean;
  /** Subtipo de armadura (body) del que RECIBE — para elegir el SFX de impacto. */
  armorSubtype?: string;
  /** Subtipo del escudo del que RECIBE — para elegir el SFX de bloqueo (metal vs default). */
  shieldSubtype?: string;
}

/**
 * Broadcast de swing melee. El server lo manda a los clients en AOI cuando una
 * entidad ejecuta un golpe (haya o no impacto), para reproducir el telegraph
 * (nudge/FX) en la dirección del golpe. El daño concreto llega por ATTACK_RESULT.
 */
export interface MeleeSwingMessage {
  type: ServerMessageType.MELEE_SWING;
  entityId: string;
  direction: Direction;
}

/**
 * Telegrafía de AoE: el server avisa a los clients en AOI la zona (centro `x,y` en tiles
 * + `size` ZxZ) donde va a caer un hechizo de área, para dibujar un decal rojo breve. El
 * daño concreto llega por el spell result. Ver docs/SPELLS.md.
 */
export interface AoeTelegraphMessage {
  type: ServerMessageType.AOE_TELEGRAPH;
  x: number;
  y: number;
  size: number;
}

/**
 * Mensaje de actualización de entidad
 */
export interface EntityUpdateMessage {
  type: ServerMessageType.ENTITY_UPDATE;
  entityId: string;
  x: number;
  y: number;
  direction: Direction;
  state: string;
}

/**
 * Mensaje de chat del servidor
 */
export interface ServerChatMessage {
  type: ServerMessageType.CHAT_MESSAGE;
  playerId: string;
  playerName: string;
  message: string;
}

/**
 * Mensaje de error
 */
export interface ErrorMessage {
  type: ServerMessageType.ERROR;
  code: string;
  message: string;
}

/**
 * Mensaje de actualización de estado de personajes conectados
 * Usado por la StatusRoom para informar qué characterIds están conectados
 */
export interface CharacterStatusUpdateMessage {
  type: ServerMessageType.CHARACTER_STATUS_UPDATE;
  characterIds: string[];
  connected: boolean;
}

export interface NpcTemplateEntry {
  id: string;
  name: string;
  bodyId: number;
  headId: number;
  weaponId: number;
  shieldId: number;
  helmId: number;
}

export interface NpcTemplateListMessage {
  type: ServerMessageType.NPC_TEMPLATE_LIST;
  templates: NpcTemplateEntry[];
}

export interface NpcDialogueMessage {
  type: ServerMessageType.NPC_DIALOGUE;
  npcId: string;
  npcName: string;
  message: string;
  /**
   * Color del texto de la burbuja (hex CSS). Default blanco si ausente. Usado
   * por facciones: rojo para "Sal de aquí, traidor" y el grito "¡Morirás!" de
   * los guardias. Ver docs/FACTIONS.md.
   */
  color?: string;
}


export interface ItemTemplateEntry {
  id: string;
  name: string;
  icon: string;
}

export interface ItemTemplateListMessage {
  type: ServerMessageType.ITEM_TEMPLATE_LIST;
  templates: ItemTemplateEntry[];
}

export interface GroundItemEntry {
  id: string;
  itemId: string;
  enchantId: string | null;
  quantity: number;
  x: number;
  y: number;
  /** Rareza rolled de la instancia en el piso (ItemRarity como number). */
  rolledRarity: number;
  /**
   * Afijos rolled de la instancia. Mapa de modifier key → componentes
   * (`v` valor + `t` tier). Vacío `{}` si no aplica.
   */
  rolledAffixes: Record<string, Array<{ v: number; t: number }>>;
}

export interface GroundItemsSyncMessage {
  type: ServerMessageType.GROUND_ITEMS_SYNC;
  items: GroundItemEntry[];
}

export interface InventorySyncMessage {
  type: ServerMessageType.INVENTORY_SYNC;
  inventory: InventorySlotData[];
}

/**
 * Mensaje para reproducir una animación sobre una entidad
 */
export interface PlayAnimationMessage {
  type: ServerMessageType.PLAY_ANIMATION;
  entityId: string; // sessionId del jugador o id de la entidad
  animationId: number;
  loop: boolean;
  back: boolean; // Si true, se dibuja debajo del personaje en vez de arriba
}

/**
 * Estadísticas del personaje con información detallada para el panel de información
 */
export interface CharacterStatsData {
  // Información básica
  name: string;
  race: string;
  class: string;
  level: number;
  experience: number;
  expForNextLevel: number;
  subclass?: string; // Placeholder por ahora
  ancestry?: string; // Placeholder por ahora

  // Vida, Mana, Stamina (actual/máximo)
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  stamina: number;
  maxStamina: number;

  // Estadísticas base (sin modificadores)
  baseHp: number;
  baseMana: number;
  baseStamina: number;

  // Estadísticas de combate (finales — base + modifiers aplicados)
  minDamage: number;
  maxDamage: number;
  physDef: number;
  basePhysDef: number;
  magicDef: number;
  baseMagicDef: number;

  // Stats finales avanzados (no tienen "base" porque el base es 0 en mob/player)
  moveSpeed: number;
  attackSpeed: number;
  castSpeed: number;
  critChance: number;
  critMultiplier: number;
  evasion: number;
  regenHP: number;
  regenMana: number;
  regenStamina: number;

  // Radio de ataque melee (final, base unarmed/arma + bonos). Ver docs/COMBAT_SYSTEMS.md.
  attackWidth: number; // ancho impar
  attackLength: number; // largo (1..5)
  attackBack: number; // tiles atrás (0..1)
  attackStaminaCost: number; // stamina por golpe (0 si noStaminaCostPhysical)

  // Stats "puros" — sólo existen como modifier acumulado (no hay base ni final
  // de entidad para estos). Vienen directamente del agregado de items+buffs.
  lifeStealPhysical: number;
  lifeStealMagic: number;
  reflectPhysicalDamage: number;
  reflectMagicDamage: number;
  healingOutput: number;
  healingReceived: number;
  magicFind: number;

  // Alcance de hechizos (bonus flat a máx / reducción de mín) + flag "sin límite".
  spellRangeBonus: number;
  unboundSpellRange: number;

  // Daño por Reino de criatura (agregado): { kingdomId: +% decimal }. Keys con
  // valor != 0. El panel arma el grupo "Daño por Reino". Ver docs/CREATURE_KINGDOMS.md.
  damageVsKingdom: Record<string, number>;

  // Magic Find por Reino (agregado): { kingdomId: +% decimal } != 0. Grupo
  // "Rareza por Reino" en el panel.
  magicFindVsKingdom: Record<string, number>;

  // Flags (0 = inactivo, >0 = activo)
  noWalkNoise: number;
  noSpellWords: number;
  noStaminaCostPhysical: number;

  // Modificadores de raza (para mostrar en tooltips)
  racePhysDefModifier: number;
  raceMagicDefModifier: number;

  // Modificadores agregados de items+buffs+sets+innatos. Incluye TODAS las keys de
  // ItemModifiers (las que tengan valor != 0). Sirve para mostrar el `(+X)`
  // al lado de cada stat en el panel.
  modifiers: Record<string, number>;

  // Desglose por FUENTE de cada modifier: raza, clase, cada item equipado, buffs y
  // sets. El panel de personaje lo usa para armar los tooltips "de dónde sale cada
  // valor" (Base + Raza + Clase + items + buffs + sets = Total). La suma de todas
  // las fuentes para una key == `modifiers[key]`.
  modifierSources: ModifierSource[];
}

/** Una fuente de modificadores (para atribución en tooltips del panel de personaje). */
export interface ModifierSource {
  kind: 'race' | 'class' | 'item' | 'buff' | 'set' | 'passive';
  label: string; // ej. "Raza: Elfo Oscuro", "Espada de Hierro", "Set: Titán (2pz)", "Árbol de Espíritu"
  modifiers: Record<string, number>; // sólo keys con valor != 0
}

export interface CharacterStatsMessage {
  type: ServerMessageType.CHARACTER_STATS;
  stats: CharacterStatsData;
}

export interface TileInfoMessage {
  type: ServerMessageType.TILE_INFO;
  x: number;
  y: number;
  players: Array<{ id: string; name: string }>;
  npcs: Array<{ id: string; name: string }>;
  items: Array<{
    id: string;
    itemId: string;
    enchantId: string | null;
    quantity: number;
    rolledRarity: number;
  }>;
}

// ============================================================================
// Skill Tree (Espíritu) Server Messages
// ============================================================================

/**
 * Datos del skill tree para sincronización
 */
export interface SkillTreeSyncData {
  /** IDs de nodos asignados */
  allocatedNodes: string[];
  /** Puntos disponibles para gastar */
  availablePoints: number;
  /** Puntos totales gastados */
  spentPoints: number;
  /** Costo en oro para resetear el árbol (escala con puntos gastados). */
  resetCost: number;
}

/**
 * Mensaje de sincronización completa del skill tree
 */
export interface SkillTreeSyncMessage {
  type: ServerMessageType.SKILL_TREE_SYNC;
  data: SkillTreeSyncData;
}

/**
 * Confirmación de que un nodo fue asignado
 */
export interface SkillNodeAllocatedMessage {
  type: ServerMessageType.SKILL_NODE_ALLOCATED;
  nodeId: string;
  remainingPoints: number;
}

/**
 * Confirmación de que el skill tree fue reseteado
 */
export interface SkillTreeResetMessage {
  type: ServerMessageType.SKILL_TREE_RESET;
  availablePoints: number;
}

// ============================================================================
// Spell System Messages (Server → Client)
// ============================================================================

export interface SpellCastResultMessage {
  type: ServerMessageType.SPELL_CAST_RESULT;
  casterId: string;
  spellId: string;
  success: boolean;
  error?: string;
  targets: Array<{
    entityId: string;
    damage?: number;
    heal?: number;
    isCrit?: boolean;
  }>;
}

export interface SpellLearnedMessage {
  type: ServerMessageType.SPELL_LEARNED;
  spellId: string;
}

export interface SpellBarSyncMessage {
  type: ServerMessageType.SPELL_BAR_SYNC;
  /** `cooldownMs` = cooldown restante (ms) del hechizo de ese slot, 0 si listo. */
  slots: Array<{ slotIndex: number; spellId: string; cooldownMs?: number }>;
}

export interface LearnedSpellsSyncMessage {
  type: ServerMessageType.LEARNED_SPELLS_SYNC;
  spells: string[];
}

export interface AuraToggledMessage {
  type: ServerMessageType.AURA_TOGGLED;
  entityId: string;
  spellId: string;
  active: boolean;
}

export interface CastStartedMessage {
  type: ServerMessageType.CAST_STARTED;
  entityId: string;
  spellId: string;
  castTime: number;
}

export interface CastInterruptedMessage {
  type: ServerMessageType.CAST_INTERRUPTED;
  entityId: string;
}

/**
 * Barco: arranca el canal de embarque/desembarque. Los clientes cercanos
 * dibujan una barra sobre el char (visible para todos). Ver docs/WATER_AND_BOATS.md.
 */
export interface BoatChannelStartedMessage {
  type: ServerMessageType.BOAT_CHANNEL_STARTED;
  entityId: string;
  kind: 'embark' | 'disembark';
  /** Duración total del canal en milisegundos. */
  duration: number;
}

export interface BoatChannelInterruptedMessage {
  type: ServerMessageType.BOAT_CHANNEL_INTERRUPTED;
  entityId: string;
}

export interface SpellWordsMessage {
  type: ServerMessageType.SPELL_WORDS;
  entityId: string;
  words: string;
  /** Optional looping animation ID to play during cast */
  castAnimationId?: number;
}

/** Snapshot of a single active buff/debuff for client display */
export interface ActiveBuffData {
  id: string;
  name: string;
  description: string;
  isDebuff: boolean;
  isAura: boolean;
  remainingMs: number;
  icon: string;
}

export interface ToggleMeditateMessage {
  type: ClientMessageType.TOGGLE_MEDITATE;
}

export interface MeditateStateMessage {
  type: ServerMessageType.MEDITATE_STATE;
  entityId: string;
  meditating: boolean;
}

/**
 * El cliente avisa que el local player hizo un input de combate que NO genera su
 * propio mensaje (típicamente apretar un hotkey de hechizo sin target válido) y
 * que debe romper su protección de aparición. Ver docs/SPAWN_PROTECTION.md.
 */
export interface BreakSpawnProtectionMessage {
  type: ClientMessageType.BREAK_SPAWN_PROTECTION;
}

/** Protección de aparición activa/inactiva para una entidad. */
export interface SpawnProtectionStateMessage {
  type: ServerMessageType.SPAWN_PROTECTION_STATE;
  entityId: string;
  active: boolean;
  /** Duración total (ms) al activarse — el cliente la usa para el contador. */
  durationMs?: number;
}

export interface LevelUpMessage {
  type: ServerMessageType.LEVEL_UP;
  entityId: string;
  newLevel: number;
  hpGain: number;
  manaGain: number;
  staminaGain: number;
}

/**
 * Ganancia de XP enviada SOLO al client del player que la cobró. El cliente
 * la muestra como burbuja overhead estilo "habla" sobre el personaje
 * (ej. "+150 EXP"). No se broadcastea a otros players (info privada).
 */
export interface ExpGainedMessage {
  type: ServerMessageType.EXP_GAINED;
  amount: number;
}

/**
 * Una entry en la lista de items del MerchantPanel. `sellPriceEffective` ya
 * incluye el fallback a `floor(buyPrice/4)` si el item no define sellPrice.
 */
export interface MerchantItemEntry {
  itemId: string;
  itemName: string;
  itemIcon: string;
  buyPrice: number;
  sellPriceEffective: number;
}

/**
 * Respuesta a OPEN_MERCHANT. Si el server autoriza la apertura, devuelve
 * el listado de items disponibles para comprar. La lista de items que el
 * player puede VENDER se calcula en el cliente desde el inventario (cualquier
 * item con sellable !== false puede venderse a CUALQUIER vendor).
 */
export interface MerchantOpenedMessage {
  type: ServerMessageType.MERCHANT_OPENED;
  npcId: string;
  merchantId: string;
  merchantName: string;
  items: MerchantItemEntry[];
}

/**
 * Una entry visible en el SpellVendorPanel. El server enriquece la SpellVendorEntry
 * con info del spell (nombre/icon/desc) + flags de gating ya pre-calculadas para
 * el player actual (clase + level), para que el cliente no duplique reglas.
 */
export interface SpellVendorItemCostView {
  itemId: string;
  itemName: string;
  itemIcon: string;
  quantity: number;
}

export interface SpellVendorEntryView {
  spellId: string;
  spellName: string;
  spellIcon: string;
  spellDescription: string;
  goldPrice: number;
  itemCosts: SpellVendorItemCostView[];
  /** Clases permitidas (whitelist), si las hay. Undefined = sin whitelist. */
  allowedClasses?: string[];
  /** Clases prohibidas (blacklist), si las hay y no hay whitelist. */
  forbiddenClasses?: string[];
  /** Razas permitidas (whitelist), si las hay. Undefined = sin whitelist. */
  allowedRaces?: string[];
  /** Razas prohibidas (blacklist), si las hay y no hay whitelist. */
  forbiddenRaces?: string[];
  /** True si el player ya aprendió este spell. */
  alreadyLearned: boolean;
  /** True si el player puede comprar (clase + level OK). Oro/items se chequean al cliente para feedback live. */
  canLearn: boolean;
  /** Texto legible para mostrar grisado en UI cuando canLearn=false. */
  reasonIfNot?: string;
}

export interface SpellVendorOpenedMessage {
  type: ServerMessageType.SPELL_VENDOR_OPENED;
  npcId: string;
  vendorId: string;
  vendorName: string;
  entries: SpellVendorEntryView[];
}

/**
 * Update de oro del player (privado). Enviado tras buy, sell, loot gold reward
 * de mob, o cualquier cambio. El cliente refresca el balance en el MerchantPanel
 * y en el HUD principal.
 */
export interface GoldUpdatedMessage {
  type: ServerMessageType.GOLD_UPDATED;
  gold: number;
  /** Diferencia desde el último update. Negativo = gasto, positivo = ganancia.
      Usado por el cliente para mostrar bubble overhead "+N oro" / "-N oro". */
  delta?: number;
}

/**
 * Broadcast a toda la sala cuando un NPC con `notifyOnRespawn=true` revive
 * (típicamente bosses). El cliente muestra un toast en el SystemConsole;
 * isBoss controla el color (oro para bosses, normal para mobs comunes que
 * tengan la flag por algún motivo).
 *
 * Cross-server broadcast multi-sala queda como TODO para bosses globales.
 */
export interface NpcRespawnedMessage {
  type: ServerMessageType.NPC_RESPAWNED;
  npcId: string;
  templateId: string;
  name: string;
  isBoss: boolean;
}

export interface ActiveBuffsSyncMessage {
  type: ServerMessageType.ACTIVE_BUFFS_SYNC;
  entityId: string;
  buffs: ActiveBuffData[];
}

/**
 * Unión de todos los tipos de mensajes del servidor
 */
/**
 * Translado same-map: el server confirmó el teleport, el cliente debe snap
 * atómicamente a (toX,toY) y reproducir la animación arrivalAnimationId.
 * Se broadcasta a clientes en AOI de fromX/Y y de toX/Y.
 */
export interface PlayerTeleportedMessage {
  type: ServerMessageType.PLAYER_TELEPORTED;
  entityId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  arrivalAnimationId: number;
  direction?: Direction;
  /**
   * Si true, el cliente local NO snapea: interpola el paso como un step normal
   * (paso forzado de 1 tile del embarque/desembarque — ver docs/WATER_AND_BOATS.md).
   */
  smooth?: boolean;
}

/**
 * Translado cross-map: enviado SOLO al cliente que viaja. El cliente debe
 * leaveAndRejoin a la room del mapa destino. La animación de llegada se
 * dispara en la room destino al final de onJoin.
 */
export interface PlayerMapChangeMessage {
  type: ServerMessageType.PLAYER_MAP_CHANGE;
  destMapId: number;
  destX: number;
  destY: number;
  arrivalAnimationId: number;
  /** Si true, la room destino debe spawnear al player en uno de los 9 candidatos al azar (anti-camp). */
  randomizeArrival?: boolean;
}

/**
 * One-shot FX server-triggered sobre una entidad (player o NPC). Reusable para
 * heal/revive del sacerdote, futuros FX de items, NPC AI, etc. El cliente
 * resuelve `fxId` contra `shared/data/particleEffects.json` y dispara el FX
 * sobre la entidad referenciada por `entityId` (sessionId de player o id de
 * NPC). Si la entidad no existe en el render set local, AnchorResolver lo
 * descarta silenciosamente.
 */
export interface PlayEntityFxMessage {
  type: ServerMessageType.PLAY_ENTITY_FX;
  entityId: string;
  fxId: string;
  /**
   * Anchor opcional a un tile fijo del mundo. Si `x`/`y` están presentes, el
   * cliente ancla el FX a ese tile (kind: 'tile') en vez de a `entityId` —
   * útil para FX que deben quedarse en una posición aunque la entidad se mueva
   * (ej. el "portal" de partida en el tile de origen de un teleport). Si están
   * ausentes, el FX se ancla a la entidad (comportamiento por defecto).
   */
  x?: number;
  y?: number;
}

/**
 * One-shot de sonido enviado por el server (hoy: broadcast del visor de sonidos admin, y a futuro
 * SFX dirigidos por server). Si `x`/`y` están presentes, el cliente lo reproduce POSICIONAL en ese
 * tile; si no, 2D directo al bus. `soundId` es un id de `SOUNDS` (shared/data/sounds.ts).
 */
export interface PlaySoundMessage {
  type: ServerMessageType.PLAY_SOUND;
  soundId: string;
  x?: number;
  y?: number;
}

/**
 * El "área-banner" del player cambió (entró a un área con nombre, o salió a una
 * sin nombre / a ninguna). Privado al player. El cliente muestra (o limpia) el
 * banner centrado arriba y, a futuro, arranca/para la música `musicId`.
 * Si `name` está vacío/ausente → limpiar banner y parar música.
 */
export interface AreaEnteredMessage {
  type: ServerMessageType.AREA_ENTERED;
  areaId?: string;
  name?: string;
  musicId?: string;
  safe: boolean;
}

/**
 * El player intentó entrar a un área que no cumple los requisitos (al caminar o
 * por translado). Privado al player. `message` ya viene en español para mostrar.
 */
export interface AreaEntryDeniedMessage {
  type: ServerMessageType.AREA_ENTRY_DENIED;
  message: string;
}

// ============================================================================
// Facciones (ver docs/FACTIONS.md)
// ============================================================================

/**
 * Stats de facción PRIVADOS del dueño (honor + enemigos eliminados + rango).
 * No van en el schema (son privados); se mandan al loguear y tras cada cambio
 * (kill PvP, ascenso, unión a facción).
 */
export interface FactionStatsMessage {
  type: ServerMessageType.FACTION_STATS;
  /** Facción actual ('aresgard' | 'elvaria' | 'rebel') o '' si es Newbie. */
  faction: string;
  /** Índice de rango en FACTION_RANKS. */
  factionRank: number;
  honor: number;
  enemiesKilled: number;
}

/**
 * El Reclutador ofrece unirse a su facción. El cliente muestra una confirmación;
 * al aceptar envía JOIN_FACTION. Privado al player que hizo click.
 */
export interface FactionJoinPromptMessage {
  type: ServerMessageType.FACTION_JOIN_PROMPT;
  factionId: string;
  /** Nombre en español de la facción, para el texto de confirmación. */
  factionLabel: string;
}

/**
 * Info de rango para el panel del Rey Faccionario. Privado al player.
 */
export interface FactionRankInfoMessage {
  type: ServerMessageType.FACTION_RANK_INFO;
  /** ID del NPC rey (para el ASCEND_RANK de vuelta). */
  npcId: string;
  faction: string;
  factionRank: number;
  enemiesKilled: number;
  honor: number;
  /** Enemigos requeridos para el próximo rango, o null si ya es Héroe. */
  nextThreshold: number | null;
  /** True si ya puede ascender (kills ≥ umbral del siguiente rango). */
  canAscend: boolean;
}

/**
 * Notificación broadcast SOLO a los players de una facción (ej. ascensos). El
 * server filtra por `player.faction === factionId` al enviar. Texto ya en español.
 */
export interface FactionBroadcastMessage {
  type: ServerMessageType.FACTION_BROADCAST;
  factionId: string;
  text: string;
}

// ===========================================================================
// QUESTS (servidor → cliente). Ver docs/QUESTS.md.
// ===========================================================================

/** Quest log completo del player (todas las activas + ready). Reemplaza el estado del cliente. */
export interface QuestLogSyncMessage {
  type: ServerMessageType.QUEST_LOG_SYNC;
  quests: QuestView[];
}

/** Un NPC ofrece una quest. El cliente abre el QuestDialogPanel en modo oferta. */
export interface QuestOfferMessage {
  type: ServerMessageType.QUEST_OFFER;
  npcId: string;
  npcName: string;
  quest: QuestView;
}

/** Avance de una quest (un objetivo subió o pasó a ready). Actualiza tracker/log. */
export interface QuestProgressMessage {
  type: ServerMessageType.QUEST_PROGRESS;
  quest: QuestView;
}

/** Quest entregada: recompensas otorgadas, para feedback en el cliente. */
export interface QuestCompletedMessage {
  type: ServerMessageType.QUEST_COMPLETED;
  questId: string;
  questName: string;
  rewards: QuestRewards;
}

// ===========================================================================
// EXCHANGE (mercado de fungibles) — Servidor → Cliente.
// Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
// ===========================================================================

/**
 * Respuesta a OPEN_EXCHANGE. Trae el catálogo de fungibles tradeables (con su
 * resumen de mercado), el oro del player y su Cajón de Retiro actual.
 */
export interface ExchangeOpenedMessage {
  type: ServerMessageType.EXCHANGE_OPENED;
  npcId: string;
  items: ExchangeItemEntry[];
  gold: number;
  claims: MarketClaimView[];
}

/**
 * Libro de órdenes + historial reciente + órdenes propias de un itemId.
 * Respuesta a EXCHANGE_VIEW_ITEM y refresh tras crear/cancelar órdenes.
 */
export interface ExchangeBookMessage {
  type: ServerMessageType.EXCHANGE_BOOK;
  itemId: string;
  /** Niveles de compra (bids), ordenados de mejor (mayor precio) a peor. */
  bids: OrderBookLevel[];
  /** Niveles de venta (asks), ordenados de mejor (menor precio) a peor. */
  asks: OrderBookLevel[];
  /** Último precio negociado, o null si nunca se tradeó. */
  lastPrice: number | null;
  /** Órdenes propias abiertas del player para este item. */
  myOrders: MarketOrderView[];
  /** Trades recientes (historial corto para la lista). */
  recentTrades: MarketTradeView[];
  /** Velas OHLC en buckets de 1 hora (vista 24h). Más viejas → más nuevas. */
  history: PriceCandle[];
  /** Velas OHLC en buckets de 1 día (vista 30d), para el toggle del gráfico. */
  historyDay: PriceCandle[];
}

/** Cajón de Retiro actualizado (tras crear/cancelar/reclamar/fills). */
export interface ExchangeClaimsMessage {
  type: ServerMessageType.EXCHANGE_CLAIMS;
  claims: MarketClaimView[];
}

// ===========================================================================
// MARKETPLACE (ítems rolled) — Servidor → Cliente
// ===========================================================================

/**
 * Respuesta a OPEN_MARKETPLACE. Trae el vocabulario para armar los filtros
 * (afijos rolables, subtipos, slots), el oro y el Cajón de Retiro.
 */
export interface MarketplaceOpenedMessage {
  type: ServerMessageType.MARKETPLACE_OPENED;
  npcId: string;
  affixOptions: AffixOption[];
  subtypes: string[];
  slots: string[];
  gold: number;
  claims: MarketClaimView[];
}

/** Resultados de una búsqueda de listados. */
export interface MarketplaceResultsMessage {
  type: ServerMessageType.MARKETPLACE_RESULTS;
  listings: MarketListingView[];
  /** Total de listados que matchean (puede exceder lo devuelto por paginación). */
  total: number;
}

/** Tasación de una instancia del inventario (respuesta a MARKETPLACE_APPRAISE). */
export interface MarketplaceAppraisalMessage {
  type: ServerMessageType.MARKETPLACE_APPRAISAL;
  slotIndex: number;
  result: AppraisalResult | null;
}

/** Búsquedas guardadas del player. */
export interface MarketplaceSavedSearchesMessage {
  type: ServerMessageType.MARKETPLACE_SAVED_SEARCHES;
  searches: SavedSearchView[];
}

/** Vigilancias de precio del player. */
export interface ExchangeWatchesMessage {
  type: ServerMessageType.EXCHANGE_WATCHES;
  watches: MarketWatchView[];
}

/** Alertas del player (lista). Se manda al abrir, al disparar y al marcar vistas. */
export interface MarketAlertsMessage {
  type: ServerMessageType.MARKET_ALERTS;
  alerts: MarketAlertView[];
}

/** Cartera del comerciante (respuesta a REQUEST_TRADER_STATS). */
export interface TraderStatsMessage {
  type: ServerMessageType.TRADER_STATS;
  stats: TraderStats;
}

export type ServerMessage =
  | MovementConfirmedMessage
  | AttackResultMessage
  | MeleeSwingMessage
  | EntityUpdateMessage
  | ServerChatMessage
  | ErrorMessage
  | CharacterStatusUpdateMessage
  | NpcTemplateListMessage
  | NpcDialogueMessage
  | ItemTemplateListMessage
  | GroundItemsSyncMessage
  | InventorySyncMessage
  | PlayAnimationMessage
  | CharacterStatsMessage
  | TileInfoMessage
  | SkillTreeSyncMessage
  | SkillNodeAllocatedMessage
  | SkillTreeResetMessage
  | SpellCastResultMessage
  | SpellLearnedMessage
  | SpellBarSyncMessage
  | LearnedSpellsSyncMessage
  | AuraToggledMessage
  | CastStartedMessage
  | CastInterruptedMessage
  | BoatChannelStartedMessage
  | BoatChannelInterruptedMessage
  | SpellWordsMessage
  | MeditateStateMessage
  | SpawnProtectionStateMessage
  | LevelUpMessage
  | ExpGainedMessage
  | MerchantOpenedMessage
  | SpellVendorOpenedMessage
  | GoldUpdatedMessage
  | NpcRespawnedMessage
  | ActiveBuffsSyncMessage
  | PlayerTeleportedMessage
  | PlayerMapChangeMessage
  | PlayEntityFxMessage
  | PlaySoundMessage
  | AreaEnteredMessage
  | AreaEntryDeniedMessage
  | FactionStatsMessage
  | FactionJoinPromptMessage
  | FactionRankInfoMessage
  | FactionBroadcastMessage
  | QuestLogSyncMessage
  | QuestOfferMessage
  | QuestProgressMessage
  | QuestCompletedMessage
  | ExchangeOpenedMessage
  | ExchangeBookMessage
  | ExchangeClaimsMessage
  | MarketplaceOpenedMessage
  | MarketplaceResultsMessage
  | MarketplaceAppraisalMessage
  | MarketplaceSavedSearchesMessage
  | ExchangeWatchesMessage
  | MarketAlertsMessage
  | TraderStatsMessage
  | PartySyncMessage
  | PartyInviteReceivedMessage
  | PartyFinderResultsMessage
  | PartyNotifyMessage;

// ===========================================================================
// PARTY (grupos entre jugadores). Ver docs/PARTY_SYSTEM.md.
// ===========================================================================

// --- Cliente → Servidor ---

export interface RequestPartyMessage {
  type: ClientMessageType.REQUEST_PARTY;
}

export interface PartyInviteMessage {
  type: ClientMessageType.PARTY_INVITE;
  /** sessionId del jugador a invitar. */
  targetSessionId: string;
}

export interface PartyInviteResponseMessage {
  type: ClientMessageType.PARTY_INVITE_RESPONSE;
  /** sessionId de quien invitó (identifica la invitación pendiente). */
  fromSessionId: string;
  accept: boolean;
}

export interface PartyLeaveMessage {
  type: ClientMessageType.PARTY_LEAVE;
}

export interface PartyKickMessage {
  type: ClientMessageType.PARTY_KICK;
  targetSessionId: string;
}

export interface PartyTransferLeaderMessage {
  type: ClientMessageType.PARTY_TRANSFER_LEADER;
  targetSessionId: string;
}

export interface PartyReorderMessage {
  type: ClientMessageType.PARTY_REORDER;
  /** Nuevo orden completo (sessionIds). Server valida que sea una permutación. */
  order: string[];
}

export interface PartySetPolicyMessage {
  type: ClientMessageType.PARTY_SET_POLICY;
  goldPolicy?: PartyDistributionPolicy;
  lootPolicy?: PartyDistributionPolicy;
  questPolicy?: PartyQuestPolicy;
}

export interface PartySetXpSharesMessage {
  type: ClientMessageType.PARTY_SET_XP_SHARES;
  /** Mapa sessionId→% (entero). null = resetear a reparto parejo. */
  shares: Record<string, number> | null;
}

export interface PartyDisbandMessage {
  type: ClientMessageType.PARTY_DISBAND;
}

export interface PartyFinderListMessage {
  type: ClientMessageType.PARTY_FINDER_LIST;
}

export interface PartyFinderCreateMessage {
  type: ClientMessageType.PARTY_FINDER_CREATE;
  visibility: PartyVisibility;
  minLevelTag?: number;
}

export interface PartyFinderSetVisibilityMessage {
  type: ClientMessageType.PARTY_FINDER_SET_VISIBILITY;
  visibility: PartyVisibility;
  minLevelTag?: number;
}

export interface PartyFinderJoinMessage {
  type: ClientMessageType.PARTY_FINDER_JOIN;
  partyId: string;
}

// --- Servidor → Cliente ---

/** Estado del party del jugador. `party === null` = no está en ningún party. */
export interface PartySyncMessage {
  type: ServerMessageType.PARTY_SYNC;
  party: PartyView | null;
}

export interface PartyInviteReceivedMessage {
  type: ServerMessageType.PARTY_INVITE_RECEIVED;
  fromSessionId: string;
  fromName: string;
}

export interface PartyFinderResultsMessage {
  type: ServerMessageType.PARTY_FINDER_RESULTS;
  parties: PartyFinderEntry[];
}

export interface PartyNotifyMessage {
  type: ServerMessageType.PARTY_NOTIFY;
  text: string;
  /** true = error/negativo (para colorear el toast). */
  isError?: boolean;
}

